import React, { useState, useEffect } from "react";
import {
  ThemeProvider,
  Typography,
  Grid,
  Box,
  Card,
  CardHeader,
  CardContent,
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { styled } from "@mui/material/styles";
import GridViewIcon from "@mui/icons-material/GridView";
import { createTheme } from "@mui/material/styles";
import { getTechnicians } from "../API/technicianAPI";
import BaseWrapper from "../../components/shared/BaseWrapper";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

const competency_scale = [
  { level: "1", description: "No capability / No interest", color: "#fa9b9b" },
  { level: "2", description: "No capability with interest", color: "#fa9b9b" },
  { level: "3", description: "Low competence", color: "#ffcccc" },
  { level: "4", description: "Beginner", color: "#ffcccc" },
  { level: "5", description: "Basic", color: "#ffcccc" },
  { level: "6", description: "Average", color: "#b6e9b1" },
  { level: "7", description: "Good", color: "#b6e9b1" },
  { level: "8", description: "Competent", color: "#b6e9b1" },
  { level: "9", description: "Advanced", color: "#82c782" },
  { level: "10", description: "Can coach", color: "#82c782" },
];

const skills = [
  "general_pest_control",
  "termite_control",
  "qpms",
  "disinfection",
  "basic_safety",
  "driving_2_wheels",
  "driving_4_wheels",
];

function ViewCompetencySkillsMatrix() {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false); // State to manage loading

  useEffect(() => {
    (async () => {
      try {
        const fetchedTechnicians = await getTechnicians();
        setTechnicians(fetchedTechnicians);
        console.log(fetchedTechnicians);
        setLoading(false);
      } catch (error) {
        setError("Failed to fetch technicians");
        setLoading(false);
      }
    })();
  }, []);

  const renderCompetencyCell = (competencyValue) => {
    const { color = "#d8d8d8" } =
      competency_scale.find((item) => item.level === competencyValue) || {};
    return {
      backgroundColor: color,
      color: competencyValue === "N/A" ? "grey" : "black",
      fontWeight: "bold",
    };
  };

  return (

    <BaseWrapper
      titleIcon={<GridViewIcon />}
      title="Operations"
      subTitle="Competency Skills Matrix"
      subTitleRoute={"/view-competency-skills-matrix"}
      // createButtonLabel="Add Technician"
      // onClickCreateButton={() => navigate("/add-technician")}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 2,
            paddingBottom: "20px",
          }}
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ marginLeft: 2 }}>
            Loading Competency Skills Matrix...
          </Typography>
        </Box>
      ) : error ? (
        <Typography variant="body1" color="error" sx={{ padding: 2 }}>
          {error}
        </Typography>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    rowSpan={2}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    Technicians
                  </TableCell>
                </TableRow>
                <TableRow>
                  {skills.map((skill) => (
                    <TableCell
                      key={skill}
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      {skill === "qpms"
                        ? skill.toUpperCase()
                        : skill
                          .replace(/_/g, " ")
                          .replace(/^\w/, (c) => c.toUpperCase())}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {technicians.map((technician, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ backgroundColor: "#FBFBFB" }}>
                      {technician.lastname}, {technician.firstname}
                    </TableCell>
                    {skills.map((skill) => {
                      const competencyValue =
                        technician.competency_level[skill] || "N/A";
                      return (
                        <TableCell
                          key={skill}
                          align="center"
                          style={renderCompetencyCell(competencyValue)}
                        >
                          {competencyValue}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <StyledHr />

          <Typography
            variant="h6"
            sx={{
              paddingBottom: "1rem",
              fontWeight: "bold",
              textTransform: "uppercase",
              fontSize: "1rem",
            }}
          >
            Competency Scale
          </Typography>
          <Grid container>
            <Grid item xs={12}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {competency_scale.map(
                        ({ level, description, color }, index) => (
                          <TableCell
                            key={index}
                            align="center"
                            sx={{
                              backgroundColor: color,
                              color: "black",
                              fontWeight: "bold",
                              width: `${100 / competency_scale.length
                                }%`,
                            }}
                          >
                            {`${level} - ${description}`}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </>
      )}
    </BaseWrapper>
    // <ThemeProvider theme={theme}>
    //   <Box sx={{ padding: "40px 60px" }}>
    //     <Card
    //       variant="outlined"
    //       style={{
    //         marginTop: "40px",
    //         marginBottom: "40px",
    //         borderRadius: 0,
    //       }}
    //     >
    //       <CardHeader
    //         title={
    //           <Box
    //             display="flex"
    //             alignItems="center"
    //             justifyContent="space-between"
    //           >
    //             <Box display="flex" alignItems="center">
    //               <GridViewIcon
    //                 sx={{
    //                   fontSize: "40px",
    //                   marginRight: "8px",
    //                 }}
    //               />
    //               <Typography variant="h6">Operations</Typography>
    //             </Box>
    //             <Breadcrumbs
    //               aria-label="breadcrumb"
    //               separator={<NavigateNextIcon fontSize="small" />}
    //               sx={{ color: "white" }}
    //             >
    //               <Typography sx={{ color: "#ffffff99" }}>
    //                 Operations
    //               </Typography>
    //               <Typography sx={{ color: "#ffffff", textDecoration: "none" }}>
    //                 View Competency Skills Matrix
    //               </Typography>
    //             </Breadcrumbs>
    //           </Box>
    //         }
    //         sx={{
    //           backgroundColor: "#1e5d45",
    //           color: "white",
    //           padding: "16px",
    //         }}
    //       />
    //       <CardContent sx={{ padding: "20px" }}>
    //         <Card variant="outlined" sx={{ padding: "20px" }}>
    //           <CardHeader
    //             title={
    //               <Typography variant="h5" sx={{ fontWeight: "bold" }}>
    //                 Competency Skills Matrix
    //               </Typography>
    //             }
    //             sx={{ marginBottom: 2 }}
    //           />
    //           {loading ? (
    //             <Box
    //               sx={{
    //                 display: "flex",
    //                 justifyContent: "center",
    //                 alignItems: "center",
    //                 padding: 2,
    //                 paddingBottom: "20px",
    //               }}
    //             >
    //               <CircularProgress />
    //               <Typography variant="body1" sx={{ marginLeft: 2 }}>
    //                 Loading Competency Skills Matrix...
    //               </Typography>
    //             </Box>
    //           ) : error ? (
    //             <Typography variant="body1" color="error" sx={{ padding: 2 }}>
    //               {error}
    //             </Typography>
    //           ) : (
    //             <>
    //               <TableContainer>
    //                 <Table>
    //                   <TableHead>
    //                     <TableRow>
    //                       <TableCell
    //                         rowSpan={2}
    //                         align="center"
    //                         sx={{
    //                           fontWeight: "bold",
    //                           backgroundColor: "#f5f5f5",
    //                         }}
    //                       >
    //                         Technicians
    //                       </TableCell>
    //                     </TableRow>
    //                     <TableRow>
    //                       {skills.map((skill) => (
    //                         <TableCell
    //                           key={skill}
    //                           align="center"
    //                           sx={{
    //                             fontWeight: "bold",
    //                             backgroundColor: "#f5f5f5",
    //                           }}
    //                         >
    //                           {skill === "qpms"
    //                             ? skill.toUpperCase()
    //                             : skill
    //                               .replace(/_/g, " ")
    //                               .replace(/^\w/, (c) => c.toUpperCase())}
    //                         </TableCell>
    //                       ))}
    //                     </TableRow>
    //                   </TableHead>
    //                   <TableBody>
    //                     {technicians.map((technician, index) => (
    //                       <TableRow key={index}>
    //                         <TableCell sx={{ backgroundColor: "#FBFBFB" }}>
    //                           {technician.lastname}, {technician.firstname}
    //                         </TableCell>
    //                         {skills.map((skill) => {
    //                           const competencyValue =
    //                             technician.competency_level[skill] || "N/A";
    //                           return (
    //                             <TableCell
    //                               key={skill}
    //                               align="center"
    //                               style={renderCompetencyCell(competencyValue)}
    //                             >
    //                               {competencyValue}
    //                             </TableCell>
    //                           );
    //                         })}
    //                       </TableRow>
    //                     ))}
    //                   </TableBody>
    //                 </Table>
    //               </TableContainer>

    //               <StyledHr />

    //               <Typography
    //                 variant="h6"
    //                 sx={{
    //                   paddingBottom: "1rem",
    //                   fontWeight: "bold",
    //                   textTransform: "uppercase",
    //                   fontSize: "1rem",
    //                 }}
    //               >
    //                 Competency Scale
    //               </Typography>
    //               <Grid container>
    //                 <Grid item xs={12}>
    //                   <TableContainer>
    //                     <Table>
    //                       <TableHead>
    //                         <TableRow>
    //                           {competency_scale.map(
    //                             ({ level, description, color }, index) => (
    //                               <TableCell
    //                                 key={index}
    //                                 align="center"
    //                                 sx={{
    //                                   backgroundColor: color,
    //                                   color: "black",
    //                                   fontWeight: "bold",
    //                                   width: `${100 / competency_scale.length
    //                                     }%`,
    //                                 }}
    //                               >
    //                                 {`${level} - ${description}`}
    //                               </TableCell>
    //                             )
    //                           )}
    //                         </TableRow>
    //                       </TableHead>
    //                     </Table>
    //                   </TableContainer>
    //                 </Grid>
    //               </Grid>
    //             </>
    //           )}
    //         </Card>
    //       </CardContent>
    //     </Card>
    //   </Box>
    // </ThemeProvider>
  );
}

export default ViewCompetencySkillsMatrix;
